/**
 * Utility functions for detecting overflow and measuring available space
 */

/**
 * Detects if an element extends beyond the visible bounds of its container
 * @param element - The element to check
 * @param container - The container to check against
 * @returns true if element overflows the container
 */
export function detectOverflow(
  element: HTMLElement,
  container: HTMLElement
): boolean {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  // Check if element bottom extends beyond container bottom
  const overflowsBottom = elementRect.bottom > containerRect.bottom;
  
  // Check if element top is above container top (for completeness)
  const overflowsTop = elementRect.top < containerRect.top;

  return overflowsBottom || overflowsTop;
}

/**
 * Calculates how much overflow exists (in pixels)
 * @param element - The element to check
 * @param container - The container to check against
 * @returns Number of pixels overflowing (positive) or space remaining (negative)
 */
export function calculateOverflowAmount(
  element: HTMLElement,
  container: HTMLElement
): number {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  // Calculate overflow at bottom
  const bottomOverflow = Math.max(0, elementRect.bottom - containerRect.bottom);
  
  // Calculate overflow at top
  const topOverflow = Math.max(0, containerRect.top - elementRect.top);

  return bottomOverflow + topOverflow;
}

